// src/pages/services/industrial-licensing.jsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { 
  ArrowRight, 
  Factory, 
  FileCheck, 
  Flame, 
  Leaf, 
  Scale, 
  HardHat, 
  Wrench,
  Calendar, 
  AlertTriangle 
} from 'lucide-react';

const IndustrialLicensing = () => {
  const navigate = useNavigate();
  const { scrollY } = useScroll();
  const imageY = useTransform(scrollY, [0, 1000], [0, -120]); // stronger parallax
  const badgeY = useTransform(scrollY, [0, 600], [0, -50]);

  const handleFreeConsultation = () => navigate('/contact');

  const licencesManaged = [
    { title: "Factory Act Licence", icon: Factory },
    { title: "Trade Licence / Business Licence", icon: Scale },
    { title: "FSSAI Registration (Food establishments)", icon: Leaf },
    { title: "Fire NOC", icon: Flame },
    { title: "Pollution Control Board Consent to Establish / Operate", icon: Leaf },
    { title: "Waste Management Authorisations", icon: FileCheck },
    { title: "Contract Labour Licence", icon: HardHat },
    { title: "Boiler and Pressure Vessel Licences", icon: Wrench },
    { title: "State-specific industry licences", icon: AlertTriangle }
  ];

  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-900 overflow-hidden">
      {/* Hero – ultra-luxurious with deeper gradient & more dynamic orbs */}
      <section className="relative min-h-screen flex items-center bg-gradient-to-br from-emerald-50 via-white to-cyan-50 pt-24 overflow-hidden">
        {/* Enhanced animated orbs */}
        <motion.div
          animate={{ scale: [1, 1.22, 1], rotate: [0, 8, 0] }}
          transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }}
          className="absolute -top-80 -left-80 w-[1200px] h-[1200px] bg-emerald-200/25 rounded-full blur-3xl"
        />
        <motion.div
          animate={{ scale: [1, 1.28, 1], rotate: [0, -10, 0] }}
          transition={{ duration: 28, repeat: Infinity, ease: "easeInOut" }}
          className="absolute -bottom-80 -right-80 w-[1300px] h-[1300px] bg-cyan-200/20 rounded-full blur-3xl"
        />
        <motion.div
          animate={{ y: [-30, 30, -30], opacity: [0.4, 0.8, 0.4] }}
          transition={{ duration: 14, repeat: Infinity }}
          className="absolute top-1/4 left-1/3 w-[500px] h-[500px] bg-emerald-300/15 rounded-full blur-2xl"
        />

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="max-w-5xl">
            <motion.div
              initial={{ opacity: 0, y: 60 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9 }}
              style={{ y: badgeY }}
              className="inline-flex items-center gap-5 bg-white/90 backdrop-blur-2xl border border-emerald-100/60 px-9 py-5 rounded-full mb-12 shadow-2xl"
            >
              <Factory className="text-[#d97925]" size={32} />
              <span className="text-[#d97925] font-bold tracking-[0.25em] uppercase text-base">
                Industrial Licensing
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 80 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.1, delay: 0.2 }}
              className="text-6xl md:text-[110px] leading-[0.92] font-black tracking-[-0.05em] mb-12 bg-gradient-to-r from-[#d97925] via-orange-600 to-red-500 bg-clip-text text-transparent"
            >
              Industrial<br />Licensing
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 70 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.1, delay: 0.4 }}
              className="text-2xl md:text-3xl text-zinc-700 max-w-4xl leading-tight font-light"
            >
              End-to-end industrial license acquisition and renewal — from Pollution Control clearances to Fire NOCs.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Main Content – richer layout */}
      <section className="py-32 md:py-40 bg-white relative">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-12 gap-16 xl:gap-24">
            {/* Left – Text & Lists */}
            <div className="lg:col-span-7 space-y-24 lg:space-y-32">
              {/* Overview */}
              <motion.div
                initial={{ opacity: 0, y: 80 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-150px" }}
                transition={{ duration: 1 }}
              >
                <h2 className="text-5xl md:text-6xl font-bold tracking-tight mb-10 bg-gradient-to-r from-[#d97925] to-red-500 bg-clip-text text-transparent">
                  Overview
                </h2>
                <div className="space-y-8 text-[18px] md:text-[19px] leading-relaxed text-zinc-800">
                  <p>
                    Operating industrial facilities in India requires a range of licences and approvals — from pollution control clearances to trade licences, fire NOCs, and hazardous waste permissions.
                  </p>
                  <p>
                    Delays in obtaining or renewing these licences can halt operations entirely.
                  </p>
                  <p className="font-medium text-zinc-900 text-xl">
                    TickBox manages the complete industrial licensing lifecycle — from documentation and liaisoning to follow-up, approval, and long-term renewal management.
                  </p>
                </div>
              </motion.div>

              {/* Licences Managed – enhanced card grid */}
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, margin: "-150px" }}
                transition={{ duration: 1, delay: 0.2 }}
              >
                <h3 className="text-4xl md:text-5xl font-bold tracking-tight mb-12 bg-gradient-to-r from-[#d97925] to-red-500 bg-clip-text text-transparent">
                  Licences Managed
                </h3>

                <div className="grid md:grid-cols-2 gap-8">
                  {licencesManaged.map((item, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 60, scale: 0.92 }}
                      whileInView={{ opacity: 1, y: 0, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.07, type: "spring", stiffness: 90 }}
                      whileHover={{ y: -12, scale: 1.03, boxShadow: "0 20px 40px -10px rgba(16,185,129,0.25)" }}
                      className="bg-white border border-emerald-100/60 rounded-3xl p-8 shadow-xl hover:shadow-2xl hover:border-emerald-300 transition-all duration-400"
                    >
                      <div className="flex items-start gap-5">
                        <div className="mt-1 w-12 h-12 rounded-2xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
                          <item.icon className="text-[#d97925]" size={26} />
                        </div>
                        <h4 className="text-xl font-semibold text-zinc-800 leading-tight">{item.title}</h4>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>

            {/* Right – Visuals with stronger parallax */}
            <motion.div
              style={{ y: imageY }}
              className="lg:col-span-5 lg:sticky lg:top-32 h-fit space-y-12"
            >
              {/* Main Image – larger & more dramatic */}
              <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-zinc-200/80">
                <img
                  src="https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=1200&auto=format&fit=crop&q=80"
                  alt="Modern Indian industrial facility with licensing compliance"
                  className="w-full h-auto object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <div className="absolute bottom-10 left-10 text-white">
                  <p className="text-2xl md:text-3xl font-bold drop-shadow-lg">All licences in place</p>
                  <p className="text-base md:text-lg opacity-90 mt-2 drop-shadow">No operational halts</p>
                </div>
              </div>

              {/* Pollution & Safety Card */}
              <motion.div
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 }}
                className="bg-gradient-to-br from-emerald-50 to-cyan-50 rounded-3xl p-10 border border-emerald-100 shadow-2xl"
              >
                <div className="flex items-center gap-6 mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-white flex items-center justify-center shadow-md">
                    <Leaf className="text-[#d97925]" size={40} />
                  </div>
                  <h4 className="text-3xl font-bold text-[#d97925]">Pollution & Safety Compliance</h4>
                </div>
                <p className="text-lg text-zinc-800 leading-relaxed">
                  Consent to Establish/Operate, Fire NOC, Waste Authorisations — all managed proactively to avoid fines or shutdowns.
                </p>
              </motion.div>

              {/* Renewal Tracking Card */}
              <motion.div
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.6 }}
                className="bg-white rounded-3xl p-10 border border-emerald-100 shadow-xl flex items-start gap-6"
              >
                <Calendar className="text-[#d97925] mt-1" size={48} />
                <div>
                  <h4 className="text-3xl font-bold mb-4 text-[#d97925]">Long-term Renewal Management</h4>
                  <p className="text-lg text-zinc-700">
                    Automated reminders, follow-up liaison, and timely renewal applications — so your operations never face licence expiry risks.
                  </p>
                </div>
              </motion.div>
            </motion.div>
          </div>

          {/* Final CTA – more dramatic with animated shine */}
          <motion.div
            initial={{ opacity: 0, y: 80 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.1 }}
            className="mt-32 md:mt-40 bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-600 text-white rounded-3xl py-24 px-8 md:px-16 text-center shadow-2xl relative overflow-hidden"
          >
            {/* Moving shine overlay */}
            <motion.div
              animate={{ x: ["-100%", "100%"] }}
              transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/15 to-transparent opacity-40"
            />

            <h3 className="text-5xl md:text-6xl font-bold tracking-tight mb-8 relative z-10">
              Ready to secure all your industrial licences?
            </h3>
            <p className="text-2xl md:text-3xl mb-12 max-w-4xl mx-auto opacity-90 relative z-10">
              From Factory Act to Pollution Board consents — full lifecycle management with zero delays or operational risks.
            </p>

            <motion.button
              whileHover={{ scale: 1.06, boxShadow: "0 30px 60px -15px rgba(16, 185, 129, 0.5)" }}
              whileTap={{ scale: 0.98 }}
              onClick={handleFreeConsultation}
              className="inline-flex items-center gap-5 px-16 py-8 bg-white text-[#d97925] font-bold text-2xl md:text-3xl rounded-3xl shadow-2xl hover:bg-emerald-50 transition-all relative z-10"
            >
              Start Your Licensing Process
              <ArrowRight size={36} />
            </motion.button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default IndustrialLicensing;